// Client-side image compression via Canvas — re-encodes as JPEG, lowering
// quality first and then dimensions, until the result fits maxBytes. PDFs
// have no equivalent here (see documentUploadLimits.ts's comment) — this is
// only ever called for image/* files.
export async function compressImageToLimit(file: File, maxBytes: number): Promise<File> {
  if (file.size <= maxBytes) return file;

  const bitmap = await createImageBitmap(file);
  let width = bitmap.width;
  let height = bitmap.height;
  let quality = 0.9;

  for (let attempt = 0; attempt < 10; attempt++) {
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) return file;
    ctx.drawImage(bitmap, 0, 0, width, height);

    const blob: Blob | null = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", quality));
    if (!blob) return file;

    if (blob.size <= maxBytes) {
      return new File([blob], renameToJpeg(file.name), { type: "image/jpeg" });
    }

    // Quality first (cheap, preserves dimensions), then shrink dimensions
    // once quality alone stops helping — matches how most client-side
    // compressors trade off visible artifacting vs. resolution loss.
    if (quality > 0.5) {
      quality -= 0.1;
    } else {
      width = Math.round(width * 0.75);
      height = Math.round(height * 0.75);
    }
  }

  return file;
}

function renameToJpeg(name: string): string {
  const base = name.replace(/\.[^./\\]+$/, "");
  return `${base}.jpg`;
}
